import { logger } from './Helpers';

/**
 * A task is a generic function that returns a Promise
 * @template T
 * @typedef {() => Promise<T>} Task
 */

/**
 * A task saved in the queue
 * @template T
 * @typedef {Object} EnqueuedTask
 * @property {Task<T>} task
 * @property {number} priority
 * @property {string} name
 */

/**
 * A task popped from the queue that is being executed
 * @typedef {Object} RunningTask
 * @property {string} name
 * @property {number} priority
 */

/**
 * An object that describes a task in history
 * @typedef {RunningTask & { at: number }} DebugTask
 */

/**
 * @template T
 * @class
 */
export class AsyncPriorityQueue {
  constructor() {
    /**
     * The max number of tasks that can be executed in parallel
     * @readonly
     */
    this.MAX_PARALLEL = 100;

    /**
     * If true, prints debug logs and populates the history object
     * @readonly
     */
    this.DEBUG_MODE = new URLSearchParams(location.search).has('debug-orch-queue');

    /** The list of tasks in the queue
     * @private
     * @type {Array<EnqueuedTask<T>>}
     */
    this._tasks = [];

    /** If the queue is in auto-draining state
     * @private
     * @type {boolean}
     */
    this._active = false;

    /** The list of tasks that are unqueued and executing
     * @private
     * @type {Array<RunningTask>}
     */
    this._running = [];

    /**
     * DEBUG ONLY
     * @public
     * @type {{ queue: Array<DebugTask>, execution: Array<DebugTask>, completion: Array<DebugTask> }}
     */
    this.history = { queue: [], execution: [], completion: [] };
  }

  /**
   * A copy of the list of tasks not yet executed
   * @returns {EnqueuedTask<T>[]}
   */
  get tasks() {
    return this._tasks.map((task) => ({ ...task }));
  }

  /**
   * The minimum priority of the tasks that are running
   * @returns {number}
   */
  get runningPriority() {
    if (this._running.length === 0) {
      return Infinity;
    }

    return Math.min(...this._running.map((el) => el.priority));
  }

  /**
   * Add a task to the end of the queue.
   * @param {string} name
   * @param {Task<T>} task
   * @param {number} priority
   */
  add(name, task, priority) {
    this._addToTaskQueue({
      name,
      task,
      priority: isNaN(priority) ? 1 : Math.max(priority, 1),
    });
  }

  /**
   * Remove the first tatask from the queue without executing it.
   * @returns {Task<T> | null}
   */
  remove() {
    const toRemove = this._tasks.shift();
    return toRemove ? toRemove.task : null;
  }

  /**
   * Put the queue in draining state.
   * The queue will execute all its tasks in order of priority.
   * The tasks will be executed in batches of MAX_PARALLEL tasks.
   */
  start() {
    this._active = true;
    this._fillExecutionQueue();
  }

  /**
   * Stop the queue from draining.
   * The queue will stop executing new tasks.
   * The tasks already in execution will continue to run until completion.
   */
  stop() {
    this._active = false;
  }

  /**
   * Add a task to the task queue based on its priority.
   * The task queue is the list of tasks that are waiting to be executed.
   * @private
   * @param {EnqueuedTask<T>} task
   */
  _addToTaskQueue(task) {
    if (this._tasks.length === 0) {
      this._tasks.push(task);
    } else if (this._tasks[this._tasks.length - 1].priority <= task.priority) {
      this._tasks.push(task);
    } else {
      for (let i = 0; i < this._tasks.length; i++) {
        if (this._tasks[i].priority > task.priority) {
          this._tasks.splice(i, 0, task);
          break;
        }
      }
    }

    if (this.DEBUG_MODE) {
      logger.debug(
        `Queued tasks ::`,
        this._tasks.map(({ name, priority }) => ({ name, priority }))
      );
      this.history.queue.push({
        name: task.name,
        priority: task.priority,
        at: performance.now(),
      });
    }

    this._onNewTaskEnqueued();
  }

  /**
   * Execute a task in the queue, handling the result and error.
   * @private
   * @param {EnqueuedTask<T>} toExecute
   * @returns {Promise<void>}
   */
  _executeTask(toExecute) {
    if (!toExecute) return;

    const runningTask = { name: toExecute.name, priority: toExecute.priority };
    this._running.push(runningTask);

    if (this.DEBUG_MODE) {
      logger.debug(
        `Running tasks ::`,
        this._running.map(({ name, priority }) => ({ name, priority }))
      );
      this.history.execution.push({
        name: toExecute.name,
        priority: toExecute.priority,
        at: performance.now(),
      });
    }

    return toExecute
      .task()
      .catch((err) => {
        logger.error(`Unhandled error during task exection ::`, {
          task: toExecute,
          error: err,
        });
        return null;
      })
      .finally(() => {
        this._onTaskComplete(runningTask);
      });
  }

  /**
   * Execute new tassk unitl the MAX_PARALLEL limit is running
   * @private
   */
  _fillExecutionQueue() {
    let next = this._tasks[0]; // Peek
    while (this._canBeExecuted(next)) {
      this._executeTask(this._tasks.shift());
      next = this._tasks[0];
    }
  }

  /**
   * @private
   * @param {EnqueuedTask<T>} task
   * @returns {boolean}
   */
  _canBeExecuted(task) {
    if (!task) return false;
    return (
      task.priority <= this.runningPriority && this._running.length < this.MAX_PARALLEL
    );
  }

  /**
   * @private
   * @param {RunningTask} completed
   */
  _onTaskComplete(completed) {
    if (this.DEBUG_MODE) {
      this.history.completion.push({
        name: completed.name,
        priority: completed.priority,
        at: performance.now(),
      });
    }

    this._running = this._running.filter((el) => el !== completed);

    if (this._active) {
      // Try to refill the execution queue with new tasks
      this._fillExecutionQueue();
    }
  }

  /**
   * @private
   */
  _onNewTaskEnqueued() {
    if (this._active) {
      // Try to put it directly in the execution queue
      this._fillExecutionQueue();
    }
  }
}
